import React, { useEffect, useState } from 'react';
import { Lock, Settings as SettingsIcon, Package } from 'lucide-react';
import Toast from './Toast';
import useToast from '../hooks/useToast';
import { getShelfCountSetting, setShelfCountSetting, unlockSettings } from '../supabase/services';
import ShelfCountLog from './settings/ShelfCountLog';
import './Settings.css';

// App-wide settings, behind the settings password. The password is checked by the
// app-settings Edge Function (never stored in the app) and kept only in this page's state,
// so leaving the page locks it again.
const Settings = () => {
  const { toastProps, showToast } = useToast();
  const [password, setPassword] = useState('');
  const [unlocked, setUnlocked] = useState(false);
  const [checking, setChecking] = useState(false);
  const [error, setError] = useState('');

  const [shelfCount, setShelfCount] = useState(null); // null until loaded
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!unlocked) return;
    getShelfCountSetting().then(result => setShelfCount(result.enabled));
  }, [unlocked]);

  const handleUnlock = async (e) => {
    e.preventDefault();
    if (!password) return;
    setChecking(true);
    setError('');
    const result = await unlockSettings(password);
    setChecking(false);
    if (result.success) {
      setUnlocked(true);
    } else {
      setError(result.message);
    }
  };

  const toggleShelfCount = async () => {
    const next = !shelfCount;
    setSaving(true);
    const result = await setShelfCountSetting(next, password);
    setSaving(false);
    if (result.success) {
      setShelfCount(next);
      showToast(next ? 'Shelf count turned on' : 'Shelf count turned off', 'success');
    } else {
      showToast(result.message, 'error');
    }
  };

  return (
    <div className="settings-page">
      <Toast {...toastProps} />
      <h2 className="settings-title"><SettingsIcon size={22} aria-hidden="true" /> Settings</h2>

      {!unlocked ? (
        <form className="settings-card settings-lock" onSubmit={handleUnlock}>
          <Lock size={28} aria-hidden="true" className="settings-lock-icon" />
          <p>Enter the settings password to continue.</p>
          <label htmlFor="settings-password" className="sr-only">Settings password</label>
          <input
            id="settings-password"
            type="password"
            className="settings-password"
            value={password}
            onChange={(e) => { setPassword(e.target.value); setError(''); }}
            autoComplete="off"
            autoFocus
            placeholder="Password"
            aria-invalid={Boolean(error)}
            aria-describedby={error ? 'settings-password-error' : undefined}
          />
          {error && <p id="settings-password-error" className="settings-error" role="alert">{error}</p>}
          <button type="submit" className="settings-btn" disabled={checking || !password}>
            {checking ? 'Checking...' : 'Unlock'}
          </button>
        </form>
      ) : (
        <>
          <section className="settings-card" aria-label="Work orders">
            <h3 className="settings-section-title">Work Orders</h3>
            <div className="settings-row">
              <div className="settings-row-text">
                <span className="settings-row-label"><Package size={16} aria-hidden="true" /> Shelf count</span>
                <span className="settings-row-help">
                  When frame or mount production is reported, ask how many are on the shelf and correct
                  Priority's stock to match. Applies to everyone. While it's on, Priority's stock figures
                  are hidden on the work orders so the count isn't copied.
                </span>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={Boolean(shelfCount)}
                aria-label="Shelf count"
                className={`settings-switch ${shelfCount ? 'on' : ''}`}
                onClick={toggleShelfCount}
                disabled={saving || shelfCount === null}
              >
                <span className="settings-switch-knob" />
                <span className="settings-switch-text">{shelfCount === null ? '...' : shelfCount ? 'On' : 'Off'}</span>
              </button>
            </div>
          </section>
          <ShelfCountLog />
        </>
      )}
    </div>
  );
};

export default Settings;
